import React, { useState, useEffect, Dispatch, SetStateAction, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'transfix-ui/components/Button'
import { Tabs, Tab, LAYOUT } from 'transfix-ui/components/DeprecatedTabs'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Toast, useToastState } from 'transfix-ui/components/Toast'
import { Box } from 'transfix-ui/core'
import { getLoadBoardPostCounts } from 'utils/actions/loadBoardPosts'
import { getShipment } from 'utils/actions/shipment'
import { updateShipment } from 'utils/actions/shipments'
import { openDialog } from 'utils/actions/ui'
import { shipmentDetailSelector } from 'apps/booking/utils/selectors/shipments'
import ShipmentStatus from 'apps/marketplace/components/ShipmentStatus'
import ShipmentStatusIcons from 'apps/marketplace/components/ShipmentStatusIcons'
import { Sidebar as SidebarContainer } from 'components/layout'
import { CheckFilledIcon, RemoveIcon } from 'transfix-icons'
import { isShipmentFlagEnabled } from 'utils/accessorialHelpers'
import { capitalize } from 'utils/capitalize'
import { getNextStatus } from 'utils/shipmentHelpers'
import { DIALOG_UPDATE_STATUSES } from 'constants/shipments'
import { handleSidebar } from '../../utils/actions/sidebar'
import { loadBoardPostsCountSelector } from '../../utils/selectors/loadBoardPosts'
import {
  sidebarStateSelector,
  sidebarShipmentSelector,
  activeRowIdSelector,
} from '../../utils/selectors/sidebar'
import { CarrierList } from './CarrierList/CarrierList'
import { ActionButtons } from './Header'
import { Posts } from './Posts/Posts'
import { ShipmentDetails } from './ShipmentDetails'

export interface IBookingSidebarProps {
  expanded: boolean
  setExpanded: Dispatch<SetStateAction<boolean>>
  onAcceptShipmentClick: (shipmentId: number) => void
  onRejectShipmentClick: (shipmentId: number) => void
}
export const Sidebar = React.memo<IBookingSidebarProps>(
  ({ expanded, onAcceptShipmentClick, onRejectShipmentClick, setExpanded }) => {
    const { error, loading, open } = useSelector(sidebarStateSelector)
    const { shipment } = useSelector(sidebarShipmentSelector)
    const { count: datPostCount } = useSelector(loadBoardPostsCountSelector)
    const {
      error: shipmentUpdateError,
      loading: shipmentUpdateLoading,
      shipmentDetails,
    } = useSelector(shipmentDetailSelector)
    const { activeRowId } = useSelector(activeRowIdSelector)
    const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()

    const id = shipment?.id
    const status = shipment?.status
    const name = status?.name
    const state = status?.state

    const SIDEBAR_TABS = {
      shipment: 'shipment',
      posts: 'posts',
    }
    const [tabState, setTabState] = useState(SIDEBAR_TABS.shipment)
    const dispatch = useDispatch()

    useEffect(() => {
      if (id) {
        dispatch(getLoadBoardPostCounts(String(id)))
      }
    }, [dispatch, id])

    useEffect(() => {
      closeToast()
    }, [activeRowId])

    useEffect(() => {
      if (
        activeRowId &&
        shipmentDetails?.id === activeRowId &&
        shipmentUpdateLoading === false &&
        shipmentDetails &&
        !shipmentUpdateError
      ) {
        dispatch(getShipment(activeRowId))
        openToast({ toastType: 'default', toastMessage: `Shipment ${activeRowId} confirmed.` })
      }
      if (
        activeRowId &&
        id === activeRowId &&
        shipmentUpdateLoading === false &&
        shipmentUpdateError
      ) {
        openToast({ toastType: 'alert', toastMessage: shipmentUpdateError })
      }
    }, [dispatch, id, activeRowId, shipmentDetails, shipmentUpdateLoading, shipmentUpdateError])

    const closeSidebar = () => dispatch(handleSidebar())
    const openBookingDialog = (dialogId: number) => dispatch(openDialog(dialogId))
    const confirmSubmittedShipment = (shipmentId: number) => {
      dispatch(updateShipment(shipmentId, { shipper_rate_confirmed_by_id: true }))
    }

    const showConfirm = status && name && ['E-Submitted', 'Quoted'].includes(name)
    const confirmESubmittedShipment = () => id && confirmSubmittedShipment(id)
    const actionMethod = showConfirm ? confirmESubmittedShipment : openBookingDialog
    let actionText = showConfirm ? 'Confirm' : 'Add Option'
    const statusUpdateModal = status && state ? getNextStatus(DIALOG_UPDATE_STATUSES, state) : false
    if (status && state !== 'E-Submitted') {
      actionText = statusUpdateModal || actionText
      if (actionText.includes('In Transit')) actionText = 'Picked Up'
      if (actionText.includes('Dispatched')) actionText = 'Dispatch'
    }

    const shipmentStatuses = {
      dropTrailer: shipment?.info?.dropTrailer,
      dropTrailerStops: shipment?.info?.dropTrailerStops,
      shipperRateType: shipment?.info?.shipperRateType,
      hot: shipment?.info?.hot,
      rolloverCount: shipment?.info?.rolloverCount,
      bouncedCount: shipment?.info?.bouncedCount,
      hasHazmat: shipment?.hasHazmat,
      hasBackhaulEligibleLane: shipment?.hasBackhaulEligibleLane,
      mustGo: isShipmentFlagEnabled(shipment?.flags || [], 'must_go'),
      sameDaySpot: isShipmentFlagEnabled(shipment?.flags || [], 'same_day_spot'),
    }

    const rejectShipmentHandler = useCallback(() => onRejectShipmentClick(id || 0), [
      id,
      onRejectShipmentClick,
    ])
    const acceptShipmentHandler = useCallback(() => onAcceptShipmentClick(id || 0), [
      id,
      onAcceptShipmentClick,
    ])

    const acceptRejectButtons = (
      <Box display='flex'>
        <Box mr={1}>
          <Button startIcon={<RemoveIcon />} color='secondary' onClick={rejectShipmentHandler}>
            Reject
          </Button>
        </Box>
        <Box ml={1}>
          <Button startIcon={<CheckFilledIcon />} color='primary' onClick={acceptShipmentHandler}>
            Accept
          </Button>
        </Box>
      </Box>
    )
    const actionButtons =
      (showConfirm && shipmentUpdateLoading) || loading ? (
        <Spinner position='relative' />
      ) : (
        <ActionButtons actionMethod={actionMethod} actionText={actionText} shipment={shipment} />
      )

    return (
      <SidebarContainer
        loading={loading}
        open={open}
        error={error}
        closeSidebar={closeSidebar}
        subtitle={
          shipment ? (
            <ShipmentStatus
              shipperName={shipment?.info?.shipperName}
              id={String(shipment.id)}
              status={shipment.status}>
              <ShipmentStatusIcons status={shipmentStatuses} />
            </ShipmentStatus>
          ) : (
            <span className='text-error'>{error}</span>
          )
        }
        actionButtons={name === 'E-Submitted' ? acceptRejectButtons : actionButtons}
        expandableContent={<CarrierList />}
        expanded={expanded}
        setExpanded={setExpanded}>
        <Tabs
          onChange={setTabState}
          value={tabState}
          layout={LAYOUT.scrollable}
          dataTest='tab-container'>
          <Tab
            value={SIDEBAR_TABS.shipment}
            label={capitalize(SIDEBAR_TABS.shipment)}
            dataTest='shipment-tab'>
            <div />
          </Tab>
          <Tab
            value={SIDEBAR_TABS.posts}
            label={capitalize(SIDEBAR_TABS.posts)}
            count={datPostCount || 0}
            dataTest='posts-tab'>
            <div />
          </Tab>
        </Tabs>
        <section className='sidebar-content'>
          {tabState === 'shipment' && <ShipmentDetails />}
          {tabState === 'posts' && <Posts />}
        </section>
        <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
      </SidebarContainer>
    )
  }
)
